/* batida-header.js (8.10.2026, Horizon P1): the behaviour of the batida.pl header, footer language pill and search layer.
   A port of the parts of Pipeline's theme.js (b2c-redesign @ 3bad9e8, 1.1 MB source) that the header used, plus the two inline
   scripts of the live sections/header.liquid (height fit + hide on scroll, search trigger wiring). No jQuery, no theme.js,
   no libraries: plain ES2015, one file, deferred, once per page.
   What is ported (the names are the live ones; the DOM hooks are the same data attributes / classes, so batida-header.css
   and the copied Batida scripts - batida-v7-menu.js, batida-v7-search.js, batida-free-delivery.js - work unchanged):
     events      theme:resize, theme:resize:width, theme:scroll (+ :up / :down), theme:scroll:lock / :unlock
     scroll lock html[data-scroll-locked] + body overflow hidden + scrollbar compensation + a touchmove guard (replaces the
                 `scroll-lock` npm package theme.js bundled)
     focus       trapFocus / removeTrapFocus / forceFocus, body.focus-enabled after Tab (the focus ring CSS keys on it)
     Header      desktop <-> phone header switch (class js__show__mobile from the width of the [data-takes-space] cells)
     Sticky      js__header__stuck (+ _backdrop) by scroll position
     Drawer      [data-drawer] + [data-drawer-toggle]: the phone menu (hamburger). The cart icon keeps data-drawer-toggle="drawer-cart"
                 for the cart drawer of P4; with no such drawer on the page it is a plain link to /cart
     HoverDisclosure / HoverLine   the desktop dropdowns / mega panel and the sliding underline of the menu
     Totals      cart price / count / "full" dot from theme:cart:change (batida-compat.js fires it) + the red count badge on the bag icons
     SearchPopdown  every [data-popdown-toggle] opens #search-popdown (focus trap, Esc, close, dim); focus back to the opener
     <popout-select>  the language pills (header, footer): list, Esc, outside click, submits Shopify's localization form
     height vars --header-height, --menu-height, --menu-backfill-height, --v7-hdr-h ... (setVars + the live "fit" script)
     hide on scroll  header slides away while scrolling down (setting v7_hide_on_scroll, class v7-hdr-hide / v7-hdr-row)
   Not ported on purpose (not used by the batida.pl header): tickers, announcement bar, toolbar, mobile sliderule, transparent
   header, cart popdown, predictive-search web components (our own search is batida-v7-search.js), accordion.
   Stability (D9): every part is wrapped so a failure in one never stops the others (console.warn, nothing thrown). */
(function () {
  'use strict';
  if (window.__batidaHeaderJs) return;
  window.__batidaHeaderJs = true;

  var doc = document, root = doc.documentElement;
  function $(s, c) { return (c || doc).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || doc).querySelectorAll(s)); }
  function safe(name, fn) { try { fn(); } catch (e) { try { console.warn('[batida-header] ' + name, e && e.message); } catch (x) {} } }
  function debounce(func, wait) {
    var t;
    return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { func.apply(c, a); }, wait); };
  }
  function fire(name, detail, target) {
    (target || doc).dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }
  function height(sel) { var el = $(sel); return el ? el.clientHeight : 0; }

  /* ------------------------------------------------------------------ theme:resize / theme:scroll */
  var lastW = window.innerWidth, lastH = window.innerHeight, rz;
  window.addEventListener('resize', function () {
    if (rz) cancelAnimationFrame(rz);
    rz = requestAnimationFrame(debounce(function () {
      fire('theme:resize');
      if (lastW !== window.innerWidth) { fire('theme:resize:width'); lastW = window.innerWidth; }
      if (lastH !== window.innerHeight) { fire('theme:resize:height'); lastH = window.innerHeight; }
    }, 50));
  });

  var prevY = window.pageYOffset, up = null, down = null, wasUp = null, wasDown = null, sc;
  function onScrollFrame() {
    var position = window.pageYOffset;
    if (position > prevY) { down = true; up = false; }
    else if (position < prevY) { down = false; up = true; }
    else { up = null; down = null; }
    prevY = position;
    doc.dispatchEvent(new CustomEvent('theme:scroll', { detail: { up: up, down: down, position: position } }));
    if (up && !wasUp) doc.dispatchEvent(new CustomEvent('theme:scroll:up', { detail: { position: position } }));
    if (down && !wasDown) doc.dispatchEvent(new CustomEvent('theme:scroll:down', { detail: { position: position } }));
    wasDown = down; wasUp = up;
  }
  window.addEventListener('scroll', function () {
    if (sc) cancelAnimationFrame(sc);
    sc = requestAnimationFrame(onScrollFrame);
  }, { passive: true });

  /* ------------------------------------------------------------------ scroll lock (theme:scroll:lock / :unlock) */
  var lockTimer = 0, lockTargets = [], savedBody = null;
  function scrollbarWidth() { return Math.max(0, window.innerWidth - root.clientWidth); }
  function touchGuard(e) {
    for (var i = 0; i < lockTargets.length; i++) { if (lockTargets[i] && lockTargets[i].contains && lockTargets[i].contains(e.target)) return; }
    if (e.target && e.target.tagName === 'TEXTAREA') return;
    if (e.cancelable) e.preventDefault();
  }
  function lock(e) {
    setTimeout(function () {
      if (lockTimer) clearTimeout(lockTimer);
      if (e && e.detail && e.detail.nodeType === 1) lockTargets.push(e.detail);
      if (!savedBody) {
        savedBody = { overflow: doc.body.style.overflow, paddingRight: doc.body.style.paddingRight };
        var w = scrollbarWidth();
        doc.body.style.overflow = 'hidden';
        if (w > 0) doc.body.style.paddingRight = w + 'px';
        doc.addEventListener('touchmove', touchGuard, { passive: false });
      }
      root.setAttribute('data-scroll-locked', '');
    });
  }
  function removeLock() {
    lockTargets = [];
    if (savedBody) {
      doc.body.style.overflow = savedBody.overflow;
      doc.body.style.paddingRight = savedBody.paddingRight;
      savedBody = null;
      doc.removeEventListener('touchmove', touchGuard, { passive: false });
    }
    root.removeAttribute('data-scroll-locked');
  }
  function unlock(e) {
    var timeout = e && e.detail;
    if (typeof timeout === 'number' && timeout) lockTimer = setTimeout(removeLock, timeout); else removeLock();
  }
  window.addEventListener('theme:scroll:lock', lock);
  window.addEventListener('theme:scroll:unlock', unlock);

  /* ------------------------------------------------------------------ focus helpers (theme.js a11y) */
  function focusable(container) {
    var els = $$('[tabindex],[draggable],a[href],area,button:enabled,input:not([type=hidden]):enabled,object,select:enabled,textarea:enabled[data-focus-element]', container);
    return els.filter(function (el) { return Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length); });
  }
  function forceFocus(el, options) {
    options = options || {};
    el.focus();
    if (options.className) el.classList.add(options.className);
    el.addEventListener('blur', function cb(ev) {
      ev.target.removeEventListener(ev.type, cb);
      if (options.className) el.classList.remove(options.className);
    });
  }
  var trapH = {};
  function removeTrapFocus() {
    doc.removeEventListener('focusin', trapH.focusin);
    doc.removeEventListener('focusout', trapH.focusout);
    doc.removeEventListener('keydown', trapH.keydown);
  }
  function trapFocus(container, options) {
    options = options || {};
    var els = focusable(container), toFocus = options.elementToFocus || container, first = els[0], last = els[els.length - 1];
    removeTrapFocus();
    trapH.focusin = function (ev) {
      if (container !== ev.target && !container.contains(ev.target) && first && first === ev.target) first.focus();
      if (ev.target !== container && ev.target !== last && ev.target !== first) return;
      doc.addEventListener('keydown', trapH.keydown);
    };
    trapH.focusout = function () { doc.removeEventListener('keydown', trapH.keydown); };
    trapH.keydown = function (ev) {
      if (ev.code !== 'Tab') return;
      if (ev.target === last && !ev.shiftKey) { ev.preventDefault(); first.focus(); }
      if ((ev.target === container || ev.target === first) && ev.shiftKey) { ev.preventDefault(); last.focus(); }
    };
    doc.addEventListener('focusout', trapH.focusout);
    doc.addEventListener('focusin', trapH.focusin);
    forceFocus(toFocus, options);
  }
  /* keyboard focus ring: batida-header.css shows it only while body.focus-enabled (Pipeline: after the first Tab) */
  doc.addEventListener('mousedown', function () { doc.body.classList.remove('focus-enabled'); });
  doc.addEventListener('keyup', function (e) { if (e.code === 'Tab') doc.body.classList.add('focus-enabled'); });

  /* ------------------------------------------------------------------ <popout-select> (the language pills) */
  var PS = { wrapper: '[data-popout]', list: '[data-popout-list]', listScroll: 'data-popout-list-scroll', toggle: 'data-popout-toggle', input: '[data-popout-input]', options: '[data-popout-option]', value: 'data-value', expanded: 'aria-expanded', current: 'aria-current', visible: 'popout-list--visible' };
  if (window.customElements && !customElements.get('popout-select')) {
    customElements.define('popout-select', class PopoutSelect extends HTMLElement {
      constructor() {
        super();
        this.container = this.querySelector(PS.wrapper);
        if (!this.container) return;
        this.popoutList = this.container.querySelector(PS.list);
        this.popoutToggle = this.container.querySelector('[' + PS.toggle + ']');
        this.outsideToggle = doc.querySelector('[' + PS.toggle + '="' + this.popoutList.id + '"]');
        this.popoutInput = this.container.querySelector(PS.input);
        this.popoutOptions = this.container.querySelectorAll(PS.options);
        var self = this;
        this.popoutToggle.addEventListener('click', function (ev) { self.toggleClick(ev); });
        if (this.outsideToggle) this.outsideToggle.addEventListener('click', function (ev) { self.toggleClick(ev); });
        Array.prototype.forEach.call(this.popoutOptions, function (el) {
          el.addEventListener('click', function (ev) { self.optionClick(ev); });
        });
        this.popoutToggle.addEventListener('focusout', function (ev) { self.toggleFocusout(ev); });
        if (this.outsideToggle) this.outsideToggle.addEventListener('focusout', function (ev) { self.toggleFocusout(ev); });
        this.popoutList.addEventListener('focusout', function (ev) { self.listFocusout(ev); });
        this.container.addEventListener('keyup', function (ev) { if (ev.code !== 'Escape') return; self.hideList(); self.popoutToggle.focus(); });
        doc.body.addEventListener('click', function (ev) {
          var inside = self.container.contains(ev.target), vis = self.popoutList.classList.contains(PS.visible);
          if (vis && !inside) self.hideList();
        });
        this.listMaxWidth();
        doc.addEventListener('theme:resize', function () { self.listMaxWidth(); });
      }
      toggleClick(ev) {
        var expanded = ev.currentTarget.getAttribute(PS.expanded) === 'true';
        ev.currentTarget.setAttribute(PS.expanded, !expanded);
        this.popoutList.classList.toggle(PS.visible);
        this.listMaxWidth();
        if (this.popoutList.hasAttribute(PS.listScroll)) {
          var l = this.popoutList;
          setTimeout(function () { fire('theme:scroll:lock', undefined, l); }, 1);
        }
      }
      toggleFocusout(ev) { if (!this.container.contains(ev.relatedTarget)) this.hideList(); }
      listFocusout(ev) {
        var child = ev.currentTarget.contains(ev.relatedTarget), vis = this.popoutList.classList.contains(PS.visible);
        if (vis && !child) this.hideList();
      }
      listMaxWidth() { this.popoutList.style.maxWidth = parseInt(doc.body.clientWidth - this.popoutList.getBoundingClientRect().left) + 'px'; }
      optionClick(ev) {
        var link = ev.target.closest(PS.options);
        if (!link || link.getAttribute('href') !== '#') return;
        ev.preventDefault();
        var v = ev.currentTarget.getAttribute(PS.value) || '';
        this.popoutInput.value = v;
        var f = this.container.closest('form');   /* submits Shopify's localization form: language_code + return_to, the visitor stays on the page */
        if (f) f.submit();
      }
      hideList() {
        this.popoutList.classList.remove(PS.visible);
        this.popoutToggle.setAttribute(PS.expanded, false);
        if (this.outsideToggle) this.outsideToggle.setAttribute(PS.expanded, false);
      }
    });
  }

  /* ------------------------------------------------------------------ the header */
  function startHeader() {
    var wrapper = $('[data-header-wrapper]');
    if (!wrapper) return;
    var headerEl = $('.theme__header', wrapper);
    var container = wrapper.closest('.shopify-section') || wrapper.parentNode;

    /* "Detect menu height early to prevent CLS" (theme.js DOMContentLoaded) + setVars / resizeVars */
    function readHeights() {
      var h = {};
      h.windowHeight = window.innerHeight;
      h.announcementHeight = height('[data-announcement-bar]');
      h.toolbarHeight = height('[data-toolbar-height]');
      h.footerHeight = height('[data-section-type*="footer"]');
      h.menuHeight = height('[data-header-height]');
      h.headerHeight = h.menuHeight + h.announcementHeight;
      h.stickyHeader = $('[data-header-sticky="sticky"]') ? h.menuHeight : 0;
      h.backfillHeight = height('[data-header-backfill]');
      return h;
    }
    function setVars(full) {
      var h = readHeights(), s = root.style;
      s.setProperty('--scrollbar-width', scrollbarWidth() + 'px');
      s.setProperty('--full-screen', h.windowHeight + 'px');
      if (full) {
        s.setProperty('--three-quarters', h.windowHeight * 0.75 + 'px');
        s.setProperty('--two-thirds', h.windowHeight * 0.66 + 'px');
        s.setProperty('--one-half', h.windowHeight * 0.5 + 'px');
        s.setProperty('--one-third', h.windowHeight * 0.33 + 'px');
        s.setProperty('--one-fifth', h.windowHeight * 0.2 + 'px');
        s.setProperty('--footer-logo', '0px');
      }
      s.setProperty('--menu-height', h.menuHeight + 'px');
      s.setProperty('--announcement-height', h.announcementHeight + 'px');
      s.setProperty('--toolbar-height', h.toolbarHeight + 'px');
      s.setProperty('--header-height', h.headerHeight + 'px');
      s.setProperty('--footer-height', h.footerHeight + 'px');
      s.setProperty('--content-full', (h.windowHeight - h.headerHeight) + 'px');
      s.setProperty('--menu-height-sticky', h.stickyHeader + 'px');
      /* if the backfill estimation is within 1px, don't force a layout shift */
      s.setProperty('--menu-backfill-height', Math.abs(h.backfillHeight - h.menuHeight) > 1 ? h.menuHeight + 'px' : 'auto');
    }
    var mh0 = headerEl ? headerEl.clientHeight : 0;
    if (mh0) root.style.setProperty('--menu-height', mh0 + 'px');
    safe('setVars', function () { setVars(true); });
    doc.addEventListener('theme:resize', function () { safe('resizeVars', function () { setVars(false); }); });

    /* ---- Header: desktop <-> phone switch (theme.js class Header) */
    safe('width check', function () {
      var desktop = $('[data-header-desktop]', wrapper);
      if (!desktop) return;
      function sum(nodes) { var w = 0; nodes.forEach(function (n) { w += n.clientWidth; }); return w; }
      function sumSplit(nodes) {
        var a = nodes.map(function (n) { return n.clientWidth; });
        if (a[0] > a[2]) a[2] = a[0]; else a[0] = a[2];
        return a.reduce(function (x, y) { return x + y; });
      }
      function getMinWidth() {
        var clone = wrapper.cloneNode(true);
        clone.classList.add('js__header__clone');
        doc.body.appendChild(clone);
        var wide = $$('[data-takes-space]', clone), min = wide.length === 3 ? sumSplit(wide) : sum(wide);
        doc.body.removeChild(clone);
        return min + wide.length * 20;
      }
      var minWidth = getMinWidth();
      function check() { wrapper.classList.toggle('js__show__mobile', doc.body.clientWidth < minWidth); }
      doc.addEventListener('theme:resize', check);
      check();
      $$('.navlink[href="#"]').forEach(function (el) { el.onclick = function (e) { e.preventDefault(); }; });
      window.dispatchEvent(new Event('resize'));
    });

    /* ---- Sticky (position: fixed from the first pixel of scroll; blur after the header's own height) */
    safe('sticky', function () {
      var type = wrapper.dataset.headerSticky, sticks = type === 'sticky', animated = type === 'directional';
      if (!sticks && !animated) return;
      var cls = wrapper.classList, stuck = false, blurred = false;
      var annH = height('[data-announcement-bar]'), hdrH = headerEl ? headerEl.clientHeight : 0;
      var blur = hdrH + annH, stickDown = sticks ? annH : hdrH + annH, stickUp = annH;
      function stick() { if (animated) cls.add('js__header__stuck--animated'); cls.add('js__header__stuck'); wrapper.setAttribute('data-header-transparent', false); stuck = true; }
      function unstick() { cls.remove('js__header__stuck'); wrapper.setAttribute('data-header-transparent', 'false'); if (animated) cls.remove('js__header__stuck--animated'); stuck = false; }
      function addBlur() { cls.add('js__header__stuck__backdrop'); blurred = true; }
      function removeBlur() { cls.remove('js__header__stuck__backdrop'); blurred = false; }
      if (sticks) {
        if (window.scrollY > stickDown) stick();
        if (window.scrollY > blur) addBlur();
      }
      doc.addEventListener('theme:scroll', function (e) {
        if (e.detail.down) {
          if (!stuck && e.detail.position > stickDown) stick();
          if (!blurred && e.detail.position > blur) addBlur();
        } else {
          if (e.detail.position <= stickUp) unstick();
          if (e.detail.position <= blur) removeBlur();
        }
      });
    });

    /* ---- Drawer (the phone menu): [data-drawer] + [data-drawer-toggle="<key>"] */
    safe('drawer', function () {
      $$('[data-drawer]', container).forEach(function (drawer) {
        var scrolls = $('[data-drawer-scrolls]', drawer), underlay = $('[data-drawer-underlay]', drawer);
        var key = drawer.dataset.drawer, buttons = $$("[data-drawer-toggle='" + key + "']");
        function setFocus() { trapFocus(drawer, { elementToFocus: $('[data-first-focus]', drawer) || undefined }); }   /* P8 (9.10): was $('data-first-focus') (no brackets, never matched): the close button gets the focus, as theme.js did */
        function show() {
          drawer.classList.remove('display-none');
          setTimeout(function () {
            buttons.forEach(function (b) { b.setAttribute('aria-expanded', true); });
            drawer.classList.add('drawer--visible');
            fire('theme:scroll:lock', scrolls);
            drawer.addEventListener('transitionend', setFocus, { once: true });
            drawer.addEventListener('transitioncancel', setFocus, { once: true });
          }, 1);
        }
        function hide() {
          if (window.theme && window.theme.state) window.theme.state.cartOpen = false;
          buttons.forEach(function (b) { b.setAttribute('aria-expanded', true); });
          drawer.classList.remove('drawer--visible');
          fire('theme:scroll:unlock', undefined, scrolls);
          doc.dispatchEvent(new CustomEvent('theme:sliderule:close'));
          removeTrapFocus();
          buttons[0] && buttons[0].focus({ preventScroll: true });
          setTimeout(function () { if (!drawer.classList.contains('drawer--visible')) drawer.classList.add('display-none'); }, 800);
        }
        buttons.forEach(function (btn) {
          btn.addEventListener('click', function (e) { e.preventDefault(); drawer.dispatchEvent(new CustomEvent('theme:drawer:toggle')); });
        });
        drawer.addEventListener('theme:drawer:toggle', function () {
          drawer.dispatchEvent(new CustomEvent(drawer.classList.contains('drawer--visible') ? 'theme:drawer:close' : 'theme:drawer:open'));
        });
        drawer.addEventListener('theme:drawer:close', hide);
        drawer.addEventListener('theme:drawer:open', show);
        drawer.addEventListener('keyup', function (e) { if (e.code === 'Escape') hide(); });
        if (underlay) underlay.addEventListener('click', hide);
      });
    });

    /* ---- HoverDisclosure: the desktop dropdowns / mega panel */
    safe('dropdowns', function () {
      $$('[data-hover-disclosure]', container).forEach(function (disclosure) {
        var key = disclosure.id, trigger = $('[data-hover-disclosure-toggle=\'' + key + '\']');
        if (!trigger) return;
        var link = $('[data-top-link]', trigger), grand = trigger.classList.contains('grandparent');
        trigger.setAttribute('aria-haspopup', true); trigger.setAttribute('aria-expanded', false); trigger.setAttribute('aria-controls', key);
        function showD() {
          if (grand) wrapper.classList.add('meganav--visible'); else wrapper.classList.remove('meganav--visible');
          trigger.setAttribute('aria-expanded', true); trigger.classList.add('is-visible'); disclosure.classList.add('is-visible');
        }
        function hideD() {
          disclosure.classList.remove('is-visible'); trigger.classList.remove('is-visible'); trigger.setAttribute('aria-expanded', false);
          wrapper.classList.remove('meganav--visible');
        }
        trigger.addEventListener('pointerenter', showD);
        if (link) link.addEventListener('focus', showD);
        trigger.addEventListener('pointerleave', hideD);
        trigger.addEventListener('focusout', function (e) { if (!trigger.contains(e.relatedTarget)) hideD(); });
        disclosure.addEventListener('keyup', function (e) { if (e.code === 'Escape') hideD(); });
        /* tablets: the first tap opens the panel, the second opens the link */
        trigger.addEventListener('touchstart', function (e) { if (!disclosure.classList.contains('is-visible')) { e.preventDefault(); showD(); } }, { passive: true });
        /* staggered fade-in of the entries */
        $$('[data-stagger]', disclosure).forEach(function (el, i) { el.style.transitionDelay = (i * 50 + 10) + 'ms'; });
        $$('[data-stagger-first]', disclosure).forEach(function (child, i) {
          var d1 = i * 150; child.style.transitionDelay = d1 + 'ms';
          $$('[data-stagger-second]', child.parentElement).forEach(function (g, i2) { g.style.transitionDelay = (d1 + (i2 + 1) * 20) + 'ms'; });
        });
        $$('[data-grid-item], [data-header-image]', disclosure).forEach(function (el, i) { el.style.transitionDelay = ((i + 1) * 80) + 'ms'; });
      });
    });

    /* ---- HoverLine: the underline that slides under the menu item */
    safe('underline', function () {
      var menu = $('[data-text-items-wrapper]', wrapper);
      if (!menu) return;
      var items = $$('[data-main-menu-text-item]', menu), outer = wrapper;
      if (!items.length) return;
      var underlineCurrent = outer.getAttribute('data-underline-current') === 'true';
      var defaultItem = underlineCurrent ? $('.menu__item.main-menu--active .navtext, .header__desktop__button.main-menu--active .navtext', menu) : null;
      var defaults = null, textBottom = null;
      function setDefault() {
        if (defaultItem) defaults = { left: Math.round(defaultItem.getBoundingClientRect().left) || null, width: defaultItem.clientWidth || null };
      }
      function setHeight() {
        var h = menu.clientHeight, text = $('.navtext', items[0]), th = text.clientHeight, tb = Math.floor(h / 2 - th / 2) - 4;
        if (textBottom !== tb) { outer.style.setProperty('--bar-text', th + 'px'); outer.style.setProperty('--bar-bottom', tb + 'px'); textBottom = tb; }
      }
      function render(w, l) { outer.style.setProperty('--bar-left', l + 'px'); outer.style.setProperty('--bar-width', w + 'px'); }
      function reset() {
        setDefault();
        if (defaults && defaults.left && defaults.width) render(defaults.width, defaults.left); else outer.style.setProperty('--bar-width', '0px');
      }
      function startBar(item) {
        setHeight();
        var active = outer.getAttribute('data-menu-active') !== 'false', left = Math.round(item.getBoundingClientRect().left), w = item.clientWidth;
        if (active) render(w, left);
        else { outer.setAttribute('data-menu-active', true); render(0, left); setTimeout(function () { render(w, left); }, 10); }
      }
      setDefault();
      (doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve()).then(function () {
        items.forEach(function (el) { el.addEventListener('pointerenter', function (e) { var t = e.target.querySelector('.navtext'); if (t) startBar(t); }); });
        menu.addEventListener('pointerleave', function () {
          outer.setAttribute('data-menu-active', false);
          setTimeout(function () { if (outer.getAttribute('data-menu-active') === 'false') reset(); }, 150);
        });
        doc.addEventListener('theme:resize', reset);
        textBottom = null; setHeight();
        if (defaults) { if (defaultItem) outer.style.setProperty('--bar-left', (Math.round(defaultItem.getBoundingClientRect().left) || 0) + 'px'); reset(); }
        else { outer.style.setProperty('--bar-left', Math.round(items[0].getBoundingClientRect().left) + 'px'); outer.style.setProperty('--bar-width', '0px'); }
        outer.style.setProperty('--bar-opacity', '1');
      });
    });

    /* ---- Totals: cart price / count / dot */
    safe('totals', function () {
      doc.addEventListener('theme:cart:change', function (event) {
        var cart = event.detail && event.detail.cart;
        if (!cart) return;
        $$('[data-header-cart-price]', container).forEach(function (el) {
          el.setAttribute('data-header-cart-price', cart.total_price);
          el.innerHTML = window.batidaMoney ? window.batidaMoney(cart.total_price) : String(cart.total_price / 100);
        });
        $$('[data-header-cart-count]', container).forEach(function (el) { el.setAttribute('data-header-cart-count', cart.item_count); el.innerHTML = '(' + cart.item_count + ')'; });
        $$('[data-header-cart-full]', container).forEach(function (el) { el.setAttribute('data-header-cart-full', cart.item_count > 0); });
      });
    });

    /* ---- the live "fit" script of sections/header.liquid (29.09): the space under the header always equals its height; hide on scroll */
    safe('fit + hide on scroll', function () {
      var w = wrapper, h = headerEl;
      if (!h) return;
      var last = -1;
      function annH() { var a = $('[data-announcement-bar]'); return a ? a.clientHeight : 0; }
      function fit() {
        var mh = Math.ceil(h.getBoundingClientRect().height);
        if (!mh || mh === last) return;
        last = mh;
        root.style.setProperty('--v7-hdr-h', mh + 'px');
        root.style.setProperty('--menu-height', mh + 'px');
        root.style.setProperty('--header-height', (mh + annH()) + 'px');
        var nv = $('.v7-hd__nav', h);
        if (nv && nv.offsetHeight) root.style.setProperty('--v7-hdr-row1', Math.round(nv.getBoundingClientRect().top - h.getBoundingClientRect().top) + 'px');
      }
      fit();
      if (window.ResizeObserver) { try { new ResizeObserver(fit).observe(h); } catch (e) {} }
      window.addEventListener('load', fit);
      window.addEventListener('resize', fit, { passive: true });

      /* search triggers: every [data-popdown-toggle] opens #search-popdown; the focus goes back to the button that opened it */
      var pdn = doc.getElementById('search-popdown'), wrap = pdn && pdn.closest('[data-search-popdown-wrap]');
      if (pdn && wrap) {
        var input = $('[data-predictive-search-input]', pdn), closeBtn = $('[data-close-popdown]', pdn), dim = $('[data-search-underlay]', wrap), opener = null;
        var triggers = $$('[data-popdown-toggle]', w);
        function showP(t) {
          opener = t;
          pdn.classList.add('is-visible'); if (dim) dim.classList.add('underlay--visible');
          trapFocus(pdn, { elementToFocus: input || undefined });
          fire('theme:scroll:lock', undefined, pdn);
        }
        function hideP() {
          pdn.classList.remove('is-visible'); if (dim) dim.classList.remove('underlay--visible');
          removeTrapFocus();
          fire('theme:scroll:unlock', undefined, pdn);
          var back = opener && opener.getClientRects().length ? opener : triggers.filter(function (t) { return t.getClientRects().length; })[0];
          if (back) back.focus();
        }
        triggers.forEach(function (t) {
          t.setAttribute('aria-haspopup', 'true'); t.setAttribute('aria-expanded', false); t.setAttribute('aria-controls', 'search-popdown');
          t.addEventListener('click', function (e) { e.preventDefault(); showP(t); });
          t.addEventListener('keyup', function (e) { if (e.code === 'Space') showP(t); });
        });
        pdn.addEventListener('keyup', function (e) { if (e.code === 'Escape') hideP(); });
        if (closeBtn) closeBtn.addEventListener('click', hideP);
        if (dim) dim.addEventListener('click', hideP);
      }

      if (w.getAttribute('data-v7-hide-on-scroll') === 'true') {
        /* Aleksandra 1.10: with the two-line header only the logo line slides away (v7-hdr-row: translate by the logo line's height,
           --v7-hdr-row1); the menu line stays, with search + bag at its end (.v7-hd__mini). An open menu never brings the logo line
           back. The phone header (below 1200 px, menu button in it) stays put. Without the two lines: the whole header. */
        var prev = window.pageYOffset, tick = false, nav = $('.v7-hd__nav', w);
        var mode = function () { return !nav ? 'all' : (nav.offsetHeight ? 'row' : 'none'); };
        var busy = function (m) {
          return root.hasAttribute('data-scroll-locked') || doc.body.classList.contains('batida-cart-drawer-open') ||
            !!$('[data-search-popdown-wrap].is-visible, .search-popdown.is-visible, .drawer.is-visible') ||
            (m === 'all' && (w.classList.contains('meganav--visible') || !!$('[aria-expanded="true"]', w)));
        };
        var show = function () { w.classList.remove('v7-hdr-hide', 'v7-hdr-row'); };
        var onScroll = function () {
          var y = window.pageYOffset, d = y - prev, m = mode();
          prev = y;
          if (m === 'none' || y <= h.clientHeight + annH() || busy(m)) { show(); return; }
          if (d > 4) { w.classList.add('v7-hdr-hide'); w.classList.toggle('v7-hdr-row', m === 'row'); }
          else if (d < -4) show();
        };
        var ms = $('[data-v7-mini-search]', w);
        if (ms) ms.addEventListener('click', function (e) {
          e.preventDefault();
          show();
          var t = $('.header__desktop__bar__r [data-popdown-toggle]', w);
          setTimeout(function () { if (t) t.click(); else location.href = ms.href; }, 320);
        });
        window.addEventListener('scroll', function () {
          if (tick) return;
          tick = true;
          requestAnimationFrame(function () { tick = false; onScroll(); });
        }, { passive: true });
        w.addEventListener('mouseenter', function () { if (mode() === 'all') show(); });
        w.addEventListener('focusin', function (e) { if (mode() === 'all' || !(e.target.closest && e.target.closest('.v7-hd__nav'))) show(); });
      }
    });
  }

  /* ---- cart count badge on the bag icons (live: batida-drawer.js, rendered after every cart change; styled by batida-cart-badge.css = in batida-header.css).
     Same selectors, same element (.batida-cart-badge), pulse on change. The slide-in cart (P4) keeps calling the same thing: idempotent. */
  var BADGE_SEL = 'a[href$="/cart"]:not([data-batida-cart-skip]), a.header__mobile__button[href$="/cart"]:not([data-batida-cart-skip]), a.navlink--cart:not([data-batida-cart-skip])';
  var lastBadgeCount = -1;
  function ensureBadgeIn(link, count) {
    var badge = link.querySelector('.batida-cart-badge');
    if (count > 0) {
      if (!badge) { badge = doc.createElement('span'); badge.className = 'batida-cart-badge'; badge.setAttribute('aria-hidden', 'true'); link.appendChild(badge); }
      if (badge.textContent !== String(count)) { badge.textContent = String(count); badge.classList.add('is-pulse'); setTimeout(function () { badge.classList.remove('is-pulse'); }, 250); }
      badge.hidden = false;
    } else if (badge) badge.hidden = true;
  }
  function renderBadge(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    lastBadgeCount = count;
    $$(BADGE_SEL).forEach(function (link) { ensureBadgeIn(link, count); });
  }
  doc.addEventListener('theme:cart:change', function (e) { safe('badge', function () { renderBadge(e.detail && e.detail.cart); }); });
  window.batidaCartBadge = renderBadge;

  /* 5.10 (Aleksandra): the orange "nowość" next to Halloween disappears after 31.10 (cached pages) - was an inline script of the live layout */
  function expireMarks() {
    var n = Date.now() / 1e3;
    $$('[data-v7-until]').forEach(function (e) { if (n >= +e.getAttribute('data-v7-until')) e.remove(); });
  }

  function start() { safe('header', startHeader); safe('marks', expireMarks); }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start); else start();
})();
